import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { ShopLayout } from "../../components/shop/ShopLayout";
import { useCart } from "../../context/CartContext";
import { fmt, apiImageUrl } from "../../lib/shopApi";

const CartPage = () => {
  const { items, setQty, removeItem, subtotalCents } = useCart();

  return (
    <ShopLayout title="Your Cart | Loctician Carlocs Shop">
      <div className="max-w-4xl mx-auto px-5 py-10 md:py-16" data-testid="cart-page">
        <h1 className="font-serif text-3xl md:text-5xl mb-10">
          Your <span className="italic text-[#C9A24B]">Cart</span>
        </h1>

        {items.length === 0 ? (
          <div className="text-center py-20" data-testid="cart-empty">
            <ShoppingBag className="w-10 h-10 text-[#C9A24B]/40 mx-auto mb-5" />
            <p className="text-[#F5EFE3]/50 mb-8">Your cart is empty — your crown deserves better.</p>
            <Link
              to="/shop"
              className="inline-block bg-[#C9A24B] text-[#1C1C1C] px-10 py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 transition-transform duration-300"
            >
              Browse the Collection
            </Link>
          </div>
        ) : (
          <>
            <div className="space-y-4 mb-10">
              {items.map((item) => (
                <div
                  key={item.key}
                  data-testid={`cart-item-${item.key}`}
                  className="flex gap-4 bg-[#232323] border border-[#C9A24B]/10 rounded-2xl p-4 items-center"
                >
                  <Link to={`/shop/product/${item.slug}`} className="w-20 h-20 rounded-xl overflow-hidden bg-[#181818] shrink-0">
                    <img src={apiImageUrl(item.image)} alt={`${item.name} - Johannesburg locs product`} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="font-serif text-base leading-snug truncate">{item.name}</p>
                    {item.size_label && <p className="text-[#F5EFE3]/45 text-xs mt-0.5">{item.size_label}</p>}
                    {Object.values(item.options || {}).length > 0 && (
                      <p className="text-[#F5EFE3]/45 text-xs mt-0.5">
                        {[item.options.length, item.options.color, item.options.thickness].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    {item.made_to_order && (
                      <p className="text-[#C9A24B]/80 text-[10px] uppercase tracking-widest mt-1">Made to order · 7-15 days</p>
                    )}
                    <p className="text-[#C9A24B] text-sm font-semibold mt-1">{fmt(item.unit_price_cents)}</p>
                  </div>
                  <div className="flex items-center border border-[#C9A24B]/25 rounded-full shrink-0">
                    <button onClick={() => setQty(item.key, item.qty - 1)} data-testid={`cart-minus-${item.key}`} className="p-2.5 text-[#F5EFE3]/60 hover:text-[#C9A24B]">
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold">{item.qty}</span>
                    <button onClick={() => setQty(item.key, item.qty + 1)} data-testid={`cart-plus-${item.key}`} className="p-2.5 text-[#F5EFE3]/60 hover:text-[#C9A24B]">
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="text-right shrink-0 w-20 hidden sm:block">
                    <p className="font-semibold">{fmt(item.unit_price_cents * item.qty)}</p>
                  </div>
                  <button onClick={() => removeItem(item.key)} data-testid={`cart-remove-${item.key}`} className="p-2 text-[#F5EFE3]/40 hover:text-red-400 transition-colors shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <div className="bg-[#232323] border border-[#C9A24B]/15 rounded-2xl p-6">
              <div className="flex justify-between mb-2 text-[#F5EFE3]/60 text-sm">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{fmt(subtotalCents)}</span>
              </div>
              <p className="text-[#F5EFE3]/40 text-xs mb-6">Delivery calculated at checkout — Paxi (3-5 days), same-day Uber in Johannesburg, or free salon pick up.</p>
              <Link
                to="/checkout"
                data-testid="go-to-checkout-btn"
                className="block text-center bg-[#C9A24B] text-[#1C1C1C] py-4 rounded-full text-xs font-bold uppercase tracking-widest hover:-translate-y-0.5 hover:shadow-[0_10px_30px_rgba(201,162,75,0.3)] transition-all duration-300"
              >
                Proceed to Checkout
              </Link>
            </div>
          </>
        )}
      </div>
    </ShopLayout>
  );
};

export default CartPage;
